import { buildUrl, useApi } from "../api";
import type { Program } from "../api";
import { Card, ErrorNote, Loading, PartyTag, formatDate, formatNumber } from "../components";

const KIND_LABEL: Record<string, string> = {
  wahlprogramm: "Wahlprogramm",
  koalitionsvertrag: "Koalitionsvertrag",
  regierungserklaerung: "Regierungserklärung",
};

export default function Programs() {
  const { data, loading, error, reload } = useApi<Program[]>(buildUrl("/api/programs"));

  return (
    <>
      <div className="page-head">
        <h1>Quellen</h1>
        <p>
          Die Dokumente, aus denen die Versprechen stammen. Zu jedem ist die Prüfsumme
          der ausgewerteten Fassung gespeichert — Parteien tauschen PDFs auf ihren
          Servern gelegentlich stillschweigend aus, und dann wäre sonst nicht mehr
          belegbar, auf welcher Fassung eine Bewertung beruht.
        </p>
      </div>

      {error ? (
        <ErrorNote message={error} onRetry={reload} />
      ) : loading ? (
        <Loading rows={7} />
      ) : (
        <Card>
          <div className="table-wrap" style={{ border: "none" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "7rem" }}>Partei</th>
                  <th>Dokument</th>
                  <th style={{ width: "8rem" }}>Art</th>
                  <th style={{ width: "6.5rem" }}>Beschlossen</th>
                  <th className="num" style={{ width: "7rem" }}>
                    Versprechen
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td>
                      <PartyTag short={p.short_name} color={p.color} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {p.source_url ? (
                          <a href={p.source_url} target="_blank" rel="noreferrer noopener">
                            {p.title}
                          </a>
                        ) : (
                          p.title
                        )}
                      </div>
                      {p.sha256 ? (
                        <div className="dip-id" style={{ marginTop: "0.15rem" }}>
                          sha256 {p.sha256.slice(0, 16)}…
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{KIND_LABEL[p.kind] ?? p.kind}</td>
                    <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                      {formatDate(p.published_date)}
                    </td>
                    <td className="num">{formatNumber(p.versprechen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Datenquellen" subtitle="Woher die parlamentarischen Daten kommen">
        <dl className="meta" style={{ marginTop: 0 }}>
          <div>
            <dt>Parlamentarische Vorgänge</dt>
            <dd style={{ fontSize: "0.9rem" }}>
              <strong>Deutscher Bundestag – DIP</strong>
              <br />
              <a href="https://dip.bundestag.de" target="_blank" rel="noreferrer noopener">
                dip.bundestag.de
              </a>
              <br />
              <span style={{ color: "var(--ink-3)", fontSize: "0.85rem" }}>
                Die Daten stehen dort kostenfrei zur Verfügung.
              </span>
            </dd>
          </div>
          <div>
            <dt>Namentliche Abstimmungen</dt>
            <dd style={{ fontSize: "0.9rem" }}>
              <strong>abgeordnetenwatch.de</strong>
              <br />
              <a
                href="https://www.abgeordnetenwatch.de/api"
                target="_blank"
                rel="noreferrer noopener"
              >
                abgeordnetenwatch.de/api
              </a>
              <br />
              <span style={{ color: "var(--ink-3)", fontSize: "0.85rem" }}>
                Bereitgestellt unter CC0 1.0.
              </span>
            </dd>
          </div>
        </dl>
      </Card>

      <Card title="Nutzungsrechte">
        <p style={{ marginTop: 0, fontSize: "0.92rem" }}>
          <strong>Amtliche Werke.</strong> PDF-Dokumente von Drucksachen und
          Plenarprotokollen sind amtliche Werke nach § 5 Absatz 2 Urheberrechtsgesetz.
          Sie werden hier nicht verändert; auszugsweise Wiedergaben sind mit
          Quellenangabe und der Bezeichnung „BT-Drs." samt Nummer gekennzeichnet.
        </p>
        <p style={{ fontSize: "0.92rem" }}>
          <strong>Wahlprogramme und Koalitionsvertrag.</strong> Diese sind
          urheberrechtlich geschützte Werke der jeweiligen Parteien. Wiedergegeben
          werden ausschließlich einzelne Zitate zum Zweck der Auseinandersetzung mit
          ihrem Inhalt (§ 51 Urheberrechtsgesetz), unverändert, mit Seitenzahl und
          Verweis auf das Originaldokument.
        </p>
        <p style={{ fontSize: "0.92rem" }}>
          <strong>Bewertungen.</strong> Die Einschätzungen zu einzelnen Versprechen
          sind Meinungsäußerungen auf Grundlage der genannten Belege, keine
          Tatsachenbehauptungen. Sie beziehen sich auf das parlamentarische Handeln
          von Fraktionen, nicht auf einzelne Personen.
        </p>
        <p style={{ fontSize: "0.85rem", color: "var(--ink-3)", marginBottom: 0 }}>
          Für die Richtigkeit der Quelldaten wird keine Gewähr übernommen. Maßgeblich
          sind die verlinkten Originaldokumente.
        </p>
      </Card>
    </>
  );
}
